<template>
  <section class="benefits-settings-form">
    <div class="benefits-field benefits-field-full">
      <label class="benefits-label" for="benefits-club-name">
        {{ t("benefitsCenter-settings-clubName") }}
        <span aria-hidden="true">*</span>
      </label>

      <KvInput
        id="benefits-club-name"
        v-model="internalValue.name"
        variant="outlined"
        size="large"
        :placeholder="t('benefitsCenter-settings-clubName')"
        @input="emitChange"
        @blur="saveGeneralField('name', internalValue.name)"
      />
    </div>

    <div class="benefits-field benefits-field-full">
      <label class="benefits-label">
        {{ t("benefitsCenter-settings-logo") }}
      </label>

      <KvImageInput
        v-model="internalValue.logo"
        variant="outlined"
        size="large"
        :title="t('benefitsCenter-settings-logoUploadTitle')"
        :description="t('benefitsCenter-settings-logoUploadDescription')"
        :button-label="t('benefitsCenter-settings-logoUploadButton')"
        :delete-label="t('benefitsCenter-settings-logoUploadDelete')"
        @input="emitAndSaveGeneralFieldDebounced('logo', internalValue.logo)"
      />
    </div>

    <div class="benefits-fields-row">
      <div class="benefits-field">
        <label class="benefits-label" for="benefits-font">
          {{ t("benefitsCenter-settings-font") }}
        </label>

        <KvSelect
          :value="resolvedFont"
          :options="fontSelectOptions"
          value-field="value"
          label-field="label"
          variant="outlined"
          size="large"
          full-width
          @input="onFontChange"
        >
          <template #option="{ option }">
            <span :style="{ fontFamily: option.value }">{{ option.label }}</span>
          </template>

          <template #selected="{ option }">
            <span :style="{ fontFamily: option.value }">{{ option.label }}</span>
          </template>
        </KvSelect>
      </div>

      <div class="benefits-field">
        <label class="benefits-label" for="benefits-color">
          {{ t("benefitsCenter-settings-color") }}
          <span aria-hidden="true">*</span>
        </label>

        <KvColorInput
          id="benefits-color"
          v-model="internalValue.primaryColor"
          variant="outlined"
          size="large"
          @input="emitAndSaveGeneralFieldDebounced('primaryColor', internalValue.primaryColor)"
        />

      </div>
    </div>

    <BenefitsBanners v-model="internalValue.banners" @input="emitAndSaveGeneralFieldDebounced('banners', internalValue.banners)" />

    <div v-if="advancedEnabled" class="benefits-advanced">
      <h3>{{ t("benefitsCenter-settings-advanced") }}</h3>

      <div v-if="statementEnabled" class="benefits-toggle-row">
        <span>{{ t("benefitsCenter-settings-showStatement") }}</span>

        <BenefitsToggle v-model="internalValue.settings.showStatement"
          @input="emitAndSaveGeneralField('showStatement', internalValue.settings.showStatement)" />
      </div>

      <div v-if="customCssEnabled" class="benefits-toggle-row">
        <span>{{ t("benefitsCenter-settings-customCss") }}</span>

        <BenefitsToggle v-model="internalValue.settings.customCss" @input="emitChange" />
      </div>
    </div>
    <BenefitsLoginSection v-model="internalValue.login" @input="emitAndSaveLoginSectionDebounced(internalValue.login)" />
    <BenefitsFaqSection v-model="internalValue.faq" @input="saveFaqSection" />
    <BenefitsTermsSection v-model="internalValue.terms" @input="saveTermsSection" />
  </section>
</template>

<script>
import KvColorInput from "../../../components/inputs/kv-color-input.vue";
import BenefitsBanners from "./benefits-banners.vue";
import BenefitsLoginSection from "./benefits-login-section.vue";
import BenefitsFaqSection from "./benefits-faq-section.vue";
import BenefitsTermsSection from "./benefits-terms-section.vue";
import BenefitsToggle from "../../shared/benefits-toggle.vue";
import KvImageInput from "../../../components/inputs/kv-image-input.vue";
import KvInput from "../../../components/inputs/kv-input.vue";
import KvSelect from "../../../components/inputs/kv-select.vue";

const STATEMENT_ENABLED = false;
const CUSTOM_CSS_ENABLED = false;
const FONT_OPTIONS = [
  "Sora",
  "Arial",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Poppins",
  "Inter",
  "Oswald",
  "Raleway",
];
const SYSTEM_FONTS = ["Arial"];

export default {
  name: "BenefitsSettingsForm",
  components: {
    KvColorInput,
    BenefitsBanners,
    BenefitsLoginSection,
    BenefitsFaqSection,
    BenefitsTermsSection,
    BenefitsToggle,
    KvImageInput,
    KvInput,
    KvSelect,
  },
  model: {
    prop: "value",
    event: "input",
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      internalValue: this.normalizeValue(this.value),
      saveTimers: {},
    };
  },
  mounted() {
    this.loadFontPreviews();
  },
  beforeDestroy() {
    Object.keys(this.saveTimers).forEach((field) => clearTimeout(this.saveTimers[field]));
  },
  watch: {
    value: {
      deep: true,
      handler(value) {
        this.internalValue = this.normalizeValue(value);
      },
    },
  },
  computed: {
    fontSelectOptions() {
      return FONT_OPTIONS.map((font) => ({
        value: font,
        label: font,
      }));
    },
    resolvedFont() {
      const font = this.internalValue.font;

      return FONT_OPTIONS.indexOf(font) !== -1 ? font : FONT_OPTIONS[0];
    },
    statementEnabled() {
      return STATEMENT_ENABLED;
    },
    customCssEnabled() {
      return CUSTOM_CSS_ENABLED;
    },
    advancedEnabled() {
      return STATEMENT_ENABLED || CUSTOM_CSS_ENABLED;
    },
  },
  methods: {
    t(term, replaces) {
      return this.$root.t(term, replaces);
    },
    onFontChange(font) {
      this.internalValue.font = font;
      this.emitAndSaveGeneralField("font", font);
    },
    loadFontPreviews() {
      const families = FONT_OPTIONS
        .filter((font) => SYSTEM_FONTS.indexOf(font) === -1)
        .map((font) => font.trim().replace(/\s+/g, "+") + ":400,600")
        .join("|");

      if (!families) {
        return;
      }

      const href = "https://fonts.googleapis.com/css?family=" + families + "&display=swap";

      if (document.querySelector('link[data-font-previews="1"]')) {
        return;
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = href;
      link.setAttribute("data-font-previews", "1");
      document.head.appendChild(link);
    },
    normalizeLogo(logo) {
      if (!logo) {
        return null;
      }

      if (typeof logo === "string") {
        return logo
          ? {
            name: "Logo",
            url: logo,
            previewUrl: logo,
          }
          : null;
      }

      if (typeof logo === "object" && (logo.url || logo.previewUrl)) {
        const url = logo.url || logo.previewUrl;

        return {
          id: logo.id || "",
          name: logo.name || "Logo",
          url,
          previewUrl: logo.previewUrl || url,
        };
      }

      return null;
    },
    serializeLogo(logo) {
      if (!logo) {
        return "";
      }

      if (typeof logo === "string") {
        return logo;
      }

      return logo.url || logo.previewUrl || "";
    },
    normalizeFaq(faq) {
      const source = faq && typeof faq === "object" && !Array.isArray(faq) ? faq : {};

      return {
        title: source.title || "",
        showTitle: source.showTitle !== false,
        image: source.image || null,
        showImage: source.showImage !== false,
        intro: source.intro || "",
        showIntro: source.showIntro !== false,
        items: Array.isArray(source.items) ? source.items : [],
      };
    },
    normalizeValue(value) {
      return {
        name: value.name || "",
        logo: this.normalizeLogo(value.logo),
        font: value.font || "default",
        primaryColor: value.primaryColor,
        banners: Array.isArray(value.banners) ? value.banners : [],
        settings: {
          showStatement: Boolean(value.settings?.showStatement),
          customCss: Boolean(value.settings?.customCss),
        },
        login: {
          background: {
            type: value.login?.background?.type || "solid",
            color: value.login?.background?.color || "#2aaa4c",
            image: value.login?.background?.image || null,
          },
          form: {
            firstNameRequired: true,
            lastNameRequired: true,
            birthDateRequired: true,
            emailRequired: value.login?.form?.emailRequired !== false,
          },
        },
        faq: this.normalizeFaq(value.faq),
        terms: value.terms || "",
      };
    },
    emitChange() {
      this.$nextTick(() => {
        this.$emit("input", {
          ...this.value,
          ...this.internalValue,
          logo: this.serializeLogo(this.internalValue.logo),
          settings: {
            ...this.value.settings,
            ...this.internalValue.settings,
          },
        });
      });
    },
    emitAndSaveGeneralField(field, value) {
      this.emitChange();
      this.saveGeneralField(field, value);
    },
    emitAndSaveGeneralFieldDebounced(field, value) {
      this.emitChange();
      clearTimeout(this.saveTimers[field]);
      this.$set(this.saveTimers, field, setTimeout(() => {
        this.saveGeneralField(field, value);
        this.$delete(this.saveTimers, field);
      }, 450));
    },
    saveGeneralField(field, value) {
      let cleanValue = typeof value === "string" ? value.trim() : value;

      if (field === "name" && !cleanValue) {
        this.internalValue.name = this.value.name;
        this.$store.dispatch("notification/error", {
          text: this.t("benefitsCenter-settings-clubNameRequired"),
        });
        return;
      }

      if (field === "banners" && Array.isArray(cleanValue)) {
        cleanValue = cleanValue
          .filter((banner) => banner && (banner.url || banner.previewUrl))
          .map((banner) => ({
            id: banner.id || "",
            name: banner.name || "",
            url: banner.url || banner.previewUrl || "",
            link: banner.link || "",
          }));
      }

      if (field === "logo") {
        cleanValue = this.serializeLogo(cleanValue);
      }

      this.$emit("save-section", "general", { [field]: cleanValue });
    },
    emitAndSaveLoginSectionDebounced(value) {
      this.emitChange();
      clearTimeout(this.saveTimers['login']);
      this.$set(this.saveTimers, 'login', setTimeout(() => {
        this.saveLoginSection(value);
        this.$delete(this.saveTimers, 'login');
      }, 450));
    },
    saveLoginSection(value) {
      this.internalValue.login = value;
      this.emitChange();
      this.$emit("save-section", "login", value);
    },
    saveFaqSection(value) {
      const faq = this.normalizeFaq(value);

      this.internalValue.faq = faq;
      this.emitChange();
      this.$emit("save-section", "faq", faq);
    },
    saveTermsSection(value) {
      this.internalValue.terms = value;
      this.emitChange();
      this.$emit("save-section", "terms", { terms: value });
    },
  },
};
</script>

<style lang="scss" scoped>
.benefits-settings-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;

  ::v-deep .kv-input,
  ::v-deep .kv-select {
    width: 100%;
  }

  ::v-deep .kv-select .kv-popup-container {
    display: block;
    width: 100%;
  }
}

.benefits-fields-row {
  display: flex;
  gap: 24px;
  width: 100%;
}

.benefits-field {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}

.benefits-field-full {
  width: 100%;
}

.benefits-label {
  color: #384250;
  font-family: "Sora", sans-serif;
  font-size: 16px;
  font-weight: 500;
  line-height: 1.3;
  margin: 0;

  span {
    color: #f04438;
    font-family: "Inter", sans-serif;
    font-size: 18px;
    font-weight: 400;
    line-height: 0;
  }
}

.benefits-input {
  align-items: center;
  background: #f9fafb;
  border: 1px solid #1f2a37;
  border-radius: 8px;
  color: #1f2a37;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  height: 44px;
  line-height: 1.3;
  padding: 12px;
  width: 100%;
}

.benefits-input {
  outline: none;

  &:focus {
    border-color: #2aaa4c;
  }
}

.benefits-reward-select-wrapper {
  position: relative;
  width: 100%;
}

.benefits-reward-select-trigger {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
  cursor: pointer;
  background: #f9fafb;
  border: 1px solid #1f2a37;
  border-radius: 8px;
  color: #1f2a37;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  height: 44px;
  line-height: 1.3;
  outline: none;
  padding: 12px;

  &:focus {
    border-color: #2aaa4c;
    box-shadow: 0 0 0 2px rgba(42, 170, 76, 0.14);
  }
}

.benefits-reward-select-icon {
  pointer-events: none;
  transition: transform 0.2s ease;

  &.is-open {
    transform: rotate(180deg);
  }
}

.benefits-reward-select-menu {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 4px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  box-shadow: 0px 5px 46.6px rgba(0, 0, 0, 0.25);
  padding: 8px;
  z-index: 1000;
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 280px;
  overflow-y: auto;
}

.benefits-reward-select-item {
  width: 100%;
  display: flex;
  align-items: center;
  padding: 10px 12px;
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 400;
  color: #1f2a37;
  text-align: left;
  transition:
    background-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: #f9fafb;
  }

  &.is-active {
    background-color: #f3f4f6;
    color: #1f2a37;
    font-weight: 600;
  }
}

.menu-fade-enter-active,
.menu-fade-leave-active {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.menu-fade-enter,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.benefits-color-preview {
  border: 1px solid #d2d6db;
  border-radius: 50%;
  display: block;
  height: 16px;
  width: 16px;
}

.benefits-advanced {
  display: flex;
  flex-direction: column;
  gap: 16px;

  h3 {
    color: #384250;
    font-family: "Sora", sans-serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 1.3;
    margin: 0;
  }
}

.benefits-toggle-row {
  align-items: center;
  align-self: flex-start;
  color: #384250;
  display: flex;
  font-family: "Inter", sans-serif;
  font-size: 14px;
  font-weight: 400;
  gap: 12px;
  justify-content: flex-start;
  line-height: 1.2;
  margin: 0;

  >span {
    min-width: 0;
    overflow-wrap: anywhere;
  }
}

@media (max-width: 700px) {
  .benefits-fields-row {
    flex-direction: column;
  }
}

@media (max-width: 480px) {
  .benefits-settings-form {
    gap: 20px;
  }

  .benefits-label {
    font-size: 14px;
  }

  .benefits-toggle-row {
    align-items: flex-start;
  }
}
</style>
